import {validateEmail, validatePhone} from "../components/helpers";
import {receipt} from "../components/receipts";
import utils from "../utils/utils";
import {getTranslation, setSingleParameter} from "./helpers";
import {completeButton, processingButton, resetButton} from "./buttons";
import {liveUpdateMutationBridge} from "../components/liveUpdateMutationBridge";
import {
    bookingIdentityFromElement,
    createBookingMutationId,
    getBookingMutationError,
    isBookingRevisionConflict,
    normalizeBookingIdentity,
} from "./booking-admin";
import {requestCoordinatedBookingPopupCloudHtml} from "./booking-popup-cloud";

const bookingPopupAction = 'wl_ajax_booking_popup';
const bookingConflictLoadingMessage = getTranslation('booking_conflict_loading');
const bookingConflictReviewMessage = getTranslation('booking_conflict_review');
const bookingConflictRefreshFailedMessage = getTranslation('booking_conflict_reopen');

function getBookingResponseMessage(response) {
    if (!response) return getTranslation('generic_error');
    if (typeof response === 'string') return response;
    if (typeof response.message === 'string') return response.message;
    if (typeof response.data === 'string') return response.data;
    if (typeof response.data?.message === 'string') return response.data.message;
    return getTranslation('generic_error');
}

function renderBookingError(form, button, message) {
    resetButton(button);
    form.find('.message').text(message);
}

export function isBookingSubmissionLocked(button) {
    return button.hasClass('processing') || button.is(':disabled');
}

export function completeBookingSubmission(button) {
    completeButton(button);
    button.attr('disabled', 'disabled');
}

function getBookingFormFirebaseId(form) {
    return String(form.find('[name="firebase_id"]').first().val() || form.data('firebase-id') || '')
        .trim()
        .toLowerCase();
}

export function applyBookingMutationResponse(form, responseData) {
    const existingFirebaseId = getBookingFormFirebaseId(form);
    if (!existingFirebaseId) {
        return;
    }

    const identity = normalizeBookingIdentity(responseData?.firebase_id, responseData?.revision);
    if (identity.firebase_id !== existingFirebaseId) {
        throw new Error(getTranslation('booking_identity_changed'));
    }

    form.attr('data-revision', identity.expected_revision);
    form.data('revision', identity.expected_revision);
    form.find('[name="expected_revision"]').val(identity.expected_revision);
    form.find('[data-firebase-id][data-revision]').each(function () {
        const element = $(this);
        if (String(element.attr('data-firebase-id') || '').trim().toLowerCase() !== identity.firebase_id) {
            return;
        }

        element.attr('data-revision', identity.expected_revision);
        element.data('revision', identity.expected_revision);
    });
}

export async function refreshBookingAfterRevisionConflict(form) {
    const firebaseId = getBookingFormFirebaseId(form);
    const popup = form.closest('.order-item-more-info');
    form.find(':input').prop('disabled', true);
    form.find('.message').text(bookingConflictLoadingMessage);

    if (!popup.length) {
        form.find('.message').text(bookingConflictRefreshFailedMessage);
        return;
    }

    try {
        const popupElement = popup.get(0);
        const request = requestCoordinatedBookingPopupCloudHtml(
            popupElement,
            {
                post_id: utils.get_post_id(),
                booking_id: Number.parseInt(
                    form.find('[name="booking_id"]').first().val() || form.data('booking-id'),
                    10,
                ) || 0,
                firebase_id: firebaseId,
            },
        );
        const html = await request.promise;
        popup.html(html);

        const refreshedForm = popup.find('[data-booking-form], .form-booking').first();
        const refreshedIdentity = bookingIdentityFromElement(refreshedForm);
        if (refreshedIdentity.firebase_id !== firebaseId) {
            throw new Error('Booking popup returned a different booking.');
        }

        refreshedForm.find('.message').text(bookingConflictReviewMessage);
        $(document.body).trigger('popup_html_loaded', [bookingPopupAction, popup]);
    } catch (error) {
        const activeForm = popup.find('[data-booking-form], .form-booking').first();
        activeForm.find(':input').prop('disabled', true);
        activeForm.find('.message').text(bookingConflictRefreshFailedMessage);
    }
}

export function createBooking(e) {
    e.preventDefault();
    const _this = $(e.currentTarget);
    let form = _this.closest('form');
    const availabilityStatus = form.attr('data-booking-availability-status');
    const bookingMessages = form.data('booking-messages') || {};
    if (availabilityStatus === 'pending' || availabilityStatus === 'invalid') {
        form.find('.message').text(availabilityStatus === 'pending' ? (bookingMessages.checking_availability || getTranslation('booking_wait_availability')) : (bookingMessages.selected_unavailable || getTranslation('booking_choose_available_interval')));
        return;
    }
    const durationInput = form.find('[name="duration_minutes"]');
    if (durationInput.length) {
        const duration = Number(durationInput.val());
        const start = Number(form.find('[name="start_time"]').val());
        const end = Number(form.find('[name="end_time"]').val());
        const provider = Number(form.find('[name="service_provider_user"]').val());
        if (![30, 60, 90].includes(duration) || !Number.isSafeInteger(start) || start <= 0 || end - start !== duration * 60 || provider <= 0) {
            form.find('.message').text(bookingMessages.choose_provider || getTranslation('booking_choose_duration_time_provider'));
            return;
        }
    }
    let data = _this.serializeArray();
    form.find('.error').removeClass('active');
    let obj = {};
    let error = 0;
    if (String(form.data('booking-id')).length) {
        //Booking already exists
        ajaxCreateBooking(data, form);
    } else if (String(form.data('user-id')).length) {
        //In case user are created from customer popup.
        obj.service_provider_user = form.find("input[name='service_provider_user']").val();
        obj.start_time = form.find("input[name='start_time']").val();
        $.each(obj, function (key, val) {
            let length = val.length;
            if (!length) {
                form.find('.error[data-type="' + key + '"]').addClass('active');
                error++;
            }
        });
        if (error === 0) ajaxCreateBooking(data, form);
    } else {
        //Validate and create new booking
        obj.first_name = form.find("input[name='first_name']").val();
        obj.last_name = form.find("input[name='last_name']").val();
        obj.phone = form.find("input[name='phone']").val();
        obj.email = form.find("input[name='email']").val();
        if (form.find("select[name='booker']").length) obj.booker = form.find("select[name='booker']").val();
        obj.service_simply_book = form.find("select[name='service_simply_book']").val();
        obj.status_simply_book = form.find("select[name='status_simply_book']").val();
        $.each(obj, function (key, val) {
            let length = val.length;
            if (!length) {
                form.find('.error[data-type="' + key + '"]').addClass('active');
                error++;
            }

            if (key === 'phone' && validatePhone(val) === false) {
                form.find('.error[data-type="' + key + '"]').addClass('active');
                error++;
            }

            if (key === 'email' && validateEmail(val) === false) {
                form.find('.error[data-type="' + key + '"]').addClass('active');
                error++;
            }
        });
        if (error === 0) ajaxCreateBooking(data, form);
    }
}

export function togglePeriodFiltering(e) {
    const _this = $(e.currentTarget);
    $('[data-period-time]').removeClass('active');
    _this.addClass('active');
    const start = _this.data('start');
    const end = _this.data('end');
    setSingleParameter('start', start);
    setSingleParameter('end', end);
    if (typeof start !== 'undefined' && typeof end !== 'undefined') {
        ajaxCreateBookingPeriod(start, end);
    }
}

function ajaxCreateBooking(data, form) {
    const button = form.find('[type="submit"]');
    if (isBookingSubmissionLocked(button)) {
        return;
    }

    data.push(
        {name: 'action', value: 'wl_ajax_create_booking_form'},
        {name: 'mutation_id', value: createBookingMutationId()},
        {name: 'permalink', value: utils.get_permalink()},
        {name: '_wpnonce', value: utils.get_nonce()},
        {name: 'post_id', value: utils.get_post_id()},
    );

    $.ajax({
        type: "POST", url:
            utils.get_ajax_url(),
        data: data,
        beforeSend: function () {
            processingButton(button);
        }, success: function (response) {
            if (response?.success === false) {
                renderBookingError(form, button, getBookingResponseMessage(response));
                return;
            }

            const responseData = response && Object.prototype.hasOwnProperty.call(response, 'data') ? response.data : response;
            if (typeof responseData === 'string' && responseData.length) {
                renderBookingError(form, button, responseData);
                return;
            }

            try {
                applyBookingMutationResponse(form, responseData);
            } catch (error) {
                renderBookingError(form, button, error.message || 'Booking response was invalid.');
                return;
            }

            const liveUpdateContext = response?.data?.live_update_context || response?.live_update_context || {post_type: 'simply_book'};
            liveUpdateMutationBridge.trigger(liveUpdateContext);

            completeBookingSubmission(button);
        }, error: function (request, status, error) {
            void status;
            void error;
            const bookingError = getBookingMutationError(
                request?.responseJSON,
                getBookingResponseMessage(request?.responseJSON),
            );
            if (isBookingRevisionConflict(bookingError)) {
                void refreshBookingAfterRevisionConflict(form);
                return;
            }
            renderBookingError(form, button, bookingError.message);
        }
    });
}

let createBookingPeriodRequestSeq = 0;

export function ajaxCreateBookingPeriod(start_date, end_date, duration_minutes = Number($('[data-create-booking-duration]').val() ?? 30)) {
    const requestSeq = ++createBookingPeriodRequestSeq;
    $.ajax({
        type: "POST",
        url: utils.get_ajax_url(),
        data: {
            start_date: start_date,
            end_date: end_date,
            duration_minutes,
            action: 'wl_ajax_create_booking_period',
            post_id: utils.get_post_id(),
            service_simply_book: utils.get_service_simply_book(),
            status_simply_book: utils.get_status_simply_book(),
            _wpnonce: utils.get_nonce(),
        },
        dataType: 'json',
        success: function (data) {
            if (requestSeq !== createBookingPeriodRequestSeq) return;
            $('#create-booking-wrapper').html(data);
        }, error: function () {
            if (requestSeq !== createBookingPeriodRequestSeq) return;
            receipt.error(getTranslation('error'));
        }
    });
}
